/* eslint-disable max-lines */
/* eslint-disable jst-gyl/check-keywords */
/**
 * request 网络请求工具
 * 更详细的 api 文档: https://github.com/umijs/umi-request
 */
import * as Sentry from '@sentry/react'
import { Modal, notification, message as $message } from 'antd'
import { getDvaApp } from 'umi'
import { extend } from 'umi-request'
import Qs from 'qs'
import { repeatReqChecker } from '@/utils/repeatReqChecker'
import { createAPI } from '@/utils/modal'
import PerfectAdminPhone from '@/components/Authorized/PerfectAdminPhone'
import NoConfigDiscountRateConfirm from '@/components/NoConfigDiscountRateConfirm'
import { checkIsInOpenPage } from '@/modules/sqmall/checkIsInOpenPage'
import { openNotice, openErpNotice, PrePayNoticeCode } from '@/modules/resource/features/puAnnouncement'
import { requestHeaderSource } from '@/utils/sourceMap'
import { getCookies, getToken } from '@/utils/token'
import { getVisitorId } from '@/utils/fingerprint'
import presentBasePrefix from '@/utils/presentBasePrefix'
import behaviorSubject from '@/modules/globals/utils/behaviorSubject'
import { IDVERIFY_OK } from '@/modules/globals/constants/behaviorSubject'
import { createRequestAutoMergedMiddleware } from '@/utils/requestAutoMerged'
import { jstLogEvent } from '@/utils/logger'
import { captureException } from '@/utils/track'
import { apiHeadersObject } from '@/utils/singleApp'
import SmsAuthModal from '@/modules/globals/components/SmsAuthModal'

// eslint-disable-next-line no-magic-numbers
export const timeOutTimer = window.PROJECT_CONFIG.sentryMode === 'prod' ? 10000 : 30000 // 10s

const errorCodeMessage = '抱歉，系统开小差了，请稍后再试~'
const successCodeMessage = '服务器成功返回请求的数据。'

export const createErrorText = 'createError'
let isShowMessage = false

const createError = ({ response, data }) => {
    return {
        response,
        data,
        isCreateError: true
    }
}

// 请求来源
const requestSource = {
    Source: requestHeaderSource()
}
// 对get接口的数组参数进行逗号拼接
const paramsSerializer = params => {
    return Qs.stringify(params, { arrayFormat: 'comma' })
}

// 设置请求加锁功能
function wrap(interceptor) {
    let resolve = null
    let reject = null

    function _clear() {
        interceptor.p = resolve = reject = null
    }

    Object.assign(interceptor, {
        lock() {
            if (!resolve) {
                interceptor.p = new Promise((_resolve, _reject) => {
                    resolve = _resolve
                    reject = _reject
                })
            }
        },
        unlock() {
            if (resolve) {
                resolve()
                _clear()
            }
        },
        clear() {
            if (reject) {
                reject('cancel')
                _clear()
            }
        }
    })
}

/**
 * 异常处理程序
 */

const ignoreMessageList = [
    'Failed to fetch',
    '已取消',
    'NetworkError when attempting to fetch resource.',
    '操作过于频繁,请稍后再试!'
]

// https://github.com/umijs/umi-request/blob/d0f3a4527d0b06ba543568b486ddbb4554f1e5c2/src/middleware/parseResponse.js#L69
// eslint-disable-next-line max-params
export const errorHandler = (error, message, body, req, type) => {
    // throw new ResponseError(copy, 'http error', body, req, 'HttpError');
    const { response = {}, data, request = {}, isCreateError = false } = error || {}

    const { url } = request || {}
    const { ok, redirected, status, statusText } = response || {}

    if (response && response.status && !isCreateError) {
        const errorText = Number(status) === 200 ? successCodeMessage : errorCodeMessage

        notification.error({
            key: window.PROJECT_CONFIG.mode !== 'prod' ? url : 'requestError',
            message: `请求错误 ${status}`,
            description: `${errorText}
            ${window.PROJECT_CONFIG.mode !== 'prod' ? url : ''}
            `
        })
    }

    if (PROJECT_CONFIG.mode === 'prod' && ignoreMessageList.indexOf(error.message) === -1 && !isCreateError) {
        if (message) {
            Sentry.setExtra('ResponseError', { error, message, type, body, req })
        } else if (response && response.status && url) {
            Sentry.setExtra('response', { url, ok, redirected, status, statusText })
        } else {
            Sentry.setExtra('error', { error: JSON.stringify(error, null, 2) })
        }

        Sentry.captureException(
            new Error(`请求错误 ${status || ''} ${url}
        `)
        )
    }

    return Promise.reject(data || response || { error, message, type, body, req })
}

// 设置请求头
export const tokenInterceptor = () => (url, options) => {
    const token = getToken()

    if (!token) {
        getDvaApp()._store.dispatch({
            type: 'login/clearLoginInfo',
            payload: {
                userAction: true
            }
        })

        const fakeResponse = {
            status: 1006,
            statusText: 'no-token',
            ok: true
        }

        return Promise.reject(createError({ response: fakeResponse }))
    }

    const headers = {
        ...options.headers,
        Authorization: token,
        'App-Version': APP_VERSION,
        ...requestSource
    }

    const { hostInfo } = window?.hostInstance?.getEnvInfo() || {}

    let container = ''

    if (hostInfo.isInErp) {
        container = 'epass'
    }

    if (hostInfo.isInEpaas) {
        container = 'erp'
    }

    if (container) {
        // 接口鉴权适配EPass
        // https://devops.aliyun.com/projex/project/44d4f6c7abd42f6e6d0ad44638/req/cfdb74027955459e67f1c9aa8c
        headers.container = container
    }
    // 短信授权逻辑同步ERP
    const sid = getCookies('sid')
    if (sid) {
        headers.sid = sid
    }

    let options_ = {
        credentials: 'omit',
        ...options,
        url,
        headers
    }

    return Promise.resolve({
        url,
        options: options_
    })
}

// 身份验证
const IdVerify = {
    isLock: false,
    show({ message, authProps }) {
        const self = this
        if (self.isLock) {
            return
        }

        self.isLock = true

        Modal.warning({
            zIndex: 1006,
            title: '身份验证',
            closable: true,
            content: (
                <div>
                    <div>{message}</div>
                    <div style={{ color: 'red' }}>如果未收到短信，请检查短信是否被拦截</div>
                </div>
            ),
            keyboard: false,
            afterClose: () => {
                self.isLock = false
            },
            okText: '我已授权',
            onOk() {
                self.isLock = false
                behaviorSubject.next({
                    type: IDVERIFY_OK
                })
            },
            ...(authProps || {})
        })
    }
}

// 身份验证
const IdVerifySms = {
    isLock: false,
    show({ code, message, authProps }) {
        const self = this
        if (self.isLock) {
            return
        }

        self.isLock = true

        const modal = createAPI(SmsAuthModal, {
            routerChangeClose: true,
            zIndex: 1006,
            dataSource: {
                code,
                message
            },
            afterClose: () => {
                self.isLock = false
            },
            onOk() {
                if (typeof authProps?.onOk === 'function') {
                    return authProps?.onOk?.()?.then?.(() => {
                        modal.close()
                        self.isLock = false
                    })
                }

                modal.close()
                self.isLock = false
                behaviorSubject.next({
                    type: IDVERIFY_OK
                })
            }
        })
    }
}

// 限流提示
const showLimitStreamTip = ({ message }) => {
    $message.warn({
        key: 'limitStream',
        content: message
    })
}

const LIMIT_STREAM_CODE = '1016' // 由于配置改造，常量先定义在这里

// 禁止预充值通知
const openForbiddenPreChargeNotice = async props => {
    if (window.top === window.self) {
        openNotice(props)
    } else {
        const topFrameEnv = await window.jst_core.env.getEnv() // 'erp'|'epaas'|'gyl'|''

        if (['erp', 'epaas'].includes(topFrameEnv)) {
            openErpNotice(props)
        } else {
            openNotice(props)
        }
    }
}

// 默认响应拦截
export const responseInterceptor = () => async (response, options) => {
    // eslint-disable-next-line no-magic-numbers
    if (response.status < 200 || response.status >= 300) {
        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise.reject({ response })
    }

    let data = null

    // 其它类型响应
    try {
        data = await response.clone().json()
    } catch (e) {
        console.error('responseInterceptor', e)
        return response.clone().blob()
    }

    if (data) {
        if (data.code === PROJECT_CONFIG.ERROR_NO_LOGIN_CODE) {
            jstLogEvent({
                element: 'REQUEST_CODE_1006',
                args: { url: options?.url, tokenIsEmpty: !options?.headers?.Authorization }
            })

            if (!isShowMessage) {
                const dur = 4
                isShowMessage = true
                $message.error('登录已过期，请尝试重新登录', dur, () => {
                    isShowMessage = false
                })
            }
            setTimeout(() => {
                // 暂时不刷新token,直接退出登陆
                getDvaApp()._store.dispatch({
                    type: 'login/clearLoginInfo',
                    payload: {
                        userAction: true
                    }
                })
                if (PROJECT_CONFIG.terminalNameSecond === PROJECT_CONFIG.sqmallTerminalName) {
                    // 如果是双奇电商城
                    // 需要登录的页面跳回首页
                    if (!checkIsInOpenPage()) {
                        history.replace('/')
                        return
                    }
                    // 允许未登录的页面 ，token过期后需要重新刷新成未登录页面

                    window.location.reload()
                    return
                }
            }, 1000)

            return Promise.reject(createError({ response, data }))
        }

        if (data.code === PROJECT_CONFIG.ERROR_REPEAT_REQUEST_CODE) {
            return Promise.reject(createError({ response, data }))
        }

        if (data.code === PROJECT_CONFIG.ERROR_NEED_PERFECT_ADMIN_CODE) {
            PerfectAdminPhone.show()
            return Promise.reject(createError({ response, data }))
        }

        if (data.code === PROJECT_CONFIG.INVALID_REQUEST) {
            captureException(`接口传参错误无效请求 ${response.url}`, {
                anchor: '接口传参错误无效请求',
                extra: {
                    params: options?.params,
                    data: options?.data
                },
                mustNotify: true
            })
        }

        if (data.code === PROJECT_CONFIG.ERROR_NEED_CHECK_ADMIN_CODE) {
            const { message } = data

            IdVerify.show({
                message,
                authProps: options?.authOptions || {}
            })
            return Promise.reject(createError({ response, data }))
        }

        // 短信验证码
        if (data?.code?.startsWith?.(`${PROJECT_CONFIG.ERROR_NEED_CHECK_ADMIN_CODE}-`)) {
            const { message } = data

            IdVerifySms.show({
                message,
                code: data.code,
                authProps: options?.authOptions || {}
            })
            return Promise.reject(createError({ response, data }))
        }

        if (data.code === PROJECT_CONFIG.ERROR_NO_DISCOUNT_RATE_CODE) {
            NoConfigDiscountRateConfirm.show()
            return Promise.reject(createError({ response, data }))
        }

        // 限流提示
        if (data.code === LIMIT_STREAM_CODE) {
            const { message } = data

            showLimitStreamTip({
                message
            })

            return Promise.reject(createError({ response, data }))
        }

        // 产品升级公告
        if (data.code === PrePayNoticeCode.findCooper || data.code === PrePayNoticeCode.freeVersion) {
            openForbiddenPreChargeNotice({
                code: data.code
            })
            return {
                ...data,
                success: false, // 注意：一定要按false处理，业务代码没有处理异常情况，reject后loading没有关闭
                message: '' // 文案给空，不提示
            }
        }
    }

    return data
}

// open 接口拦截器
export const openInterceptor = (url, options) => {
    let options_ = options

    options_ = {
        credentials: 'omit',
        ...options_
    }

    return Promise.resolve({
        url,
        options: options_
    })
}

// 开放拦截器
export const openResponseInterceptor = async response => {
    let data = null

    // 其它类型响应
    try {
        data = await response.clone().json()
    } catch (e) {
        console.error('openResponseInterceptor', e)
        return response.clone().blob()
    }
    return data
}

// api/op 服务
const opRequest = extend({
    prefix: presentBasePrefix(PROJECT_CONFIG.baseOpApi),
    errorHandler,
    paramsSerializer
})

opRequest.interceptors.request.use(tokenInterceptor(opRequest), { global: false })
opRequest.interceptors.response.use(responseInterceptor(opRequest), {
    global: false
})

wrap(opRequest.interceptors.request)
wrap(opRequest.interceptors.response)

// 开放 op 请求
const openOpRequest = extend({
    prefix: presentBasePrefix(PROJECT_CONFIG.baseOpApi),
    errorHandler,
    paramsSerializer,
    headers: { 'Content-Type': 'application/json', ...requestSource }
})

openOpRequest.interceptors.request.use(openInterceptor, { global: false })
openOpRequest.interceptors.response.use(openResponseInterceptor, { global: false })

/**
 * 以下为新服务调用实例,迁移完成后移除以上旧服务, 注：原撮合服务重名，旧服务已经删除
 */

// api/goodsbasic 商品基础服务
const goodsBasicRequest = extend({
    prefix: presentBasePrefix(PROJECT_CONFIG.baseGoodsBasicApi),
    errorHandler,
    paramsSerializer
})

goodsBasicRequest.use((ctx, next) => createRequestAutoMergedMiddleware(ctx, next, goodsBasicRequest), { global: false })
goodsBasicRequest.interceptors.request.use(tokenInterceptor(goodsBasicRequest), { global: false })
goodsBasicRequest.interceptors.request.use(repeatReqChecker, { global: false })
goodsBasicRequest.interceptors.response.use(responseInterceptor(goodsBasicRequest), {
    global: false
})

wrap(goodsBasicRequest.interceptors.request)
wrap(goodsBasicRequest.interceptors.response)

// api/goods 商品服务
const goodsRequest = extend({
    prefix: presentBasePrefix(PROJECT_CONFIG.baseGoodsApi),
    errorHandler,
    paramsSerializer
})

goodsRequest.use((ctx, next) => createRequestAutoMergedMiddleware(ctx, next, goodsRequest), { global: false })
goodsRequest.interceptors.request.use(tokenInterceptor(goodsRequest), { global: false })
goodsRequest.interceptors.request.use(repeatReqChecker, { global: false })
goodsRequest.interceptors.response.use(responseInterceptor(goodsRequest), {
    global: false
})

wrap(goodsRequest.interceptors.request)
wrap(goodsRequest.interceptors.response)

// 开放请求
const openGoodsRequest = extend({
    prefix: presentBasePrefix(PROJECT_CONFIG.baseGoodsApi),
    errorHandler,
    paramsSerializer,
    headers: { 'Content-Type': 'application/json', ...requestSource }
})

openGoodsRequest.use((ctx, next) => createRequestAutoMergedMiddleware(ctx, next, openGoodsRequest), { global: false })
openGoodsRequest.interceptors.request.use(openInterceptor, { global: false })
openGoodsRequest.interceptors.response.use(openResponseInterceptor, { global: false })

// api/Company 商家服务
const companyRequest = extend({
    prefix: presentBasePrefix(PROJECT_CONFIG.baseCompanyApi),
    errorHandler,
    paramsSerializer
})

companyRequest.interceptors.request.use(tokenInterceptor(companyRequest), { global: false })
companyRequest.interceptors.request.use(repeatReqChecker, { global: false })
companyRequest.interceptors.response.use(responseInterceptor(companyRequest), {
    global: false
})

wrap(companyRequest.interceptors.request)
wrap(companyRequest.interceptors.response)

// 不需要前缀请求, eg:/api/xxx/xxx
const noPrefixRequest = extend({
    prefix: '',
    errorHandler,
    paramsSerializer
})

noPrefixRequest.interceptors.request.use(tokenInterceptor(noPrefixRequest), { global: false })
noPrefixRequest.interceptors.request.use(repeatReqChecker, { global: false })
noPrefixRequest.interceptors.response.use(responseInterceptor(noPrefixRequest), {
    global: false
})

wrap(noPrefixRequest.interceptors.request)
wrap(noPrefixRequest.interceptors.response)

// 开放请求
const openCompanyRequest = extend({
    prefix: presentBasePrefix(PROJECT_CONFIG.baseCompanyApi),
    errorHandler,
    paramsSerializer,
    headers: { 'Content-Type': 'application/json', ...requestSource }
})

openCompanyRequest.interceptors.request.use(openInterceptor, { global: false })
openCompanyRequest.interceptors.response.use(openResponseInterceptor, { global: false })

// inner 塔内服务
const towerRequest = extend({
    prefix: presentBasePrefix(PROJECT_CONFIG.baseTowerApi),
    errorHandler,
    paramsSerializer
})

towerRequest.interceptors.request.use(tokenInterceptor(towerRequest), { global: false })
towerRequest.interceptors.response.use(responseInterceptor(towerRequest), {
    global: false
})
// inner 塔内垂直分销服务
const towerDrpRequest = extend({
    prefix: presentBasePrefix(PROJECT_CONFIG.baseTowerDrpApi),
    errorHandler,
    paramsSerializer
})

towerDrpRequest.interceptors.request.use(tokenInterceptor(towerDrpRequest), { global: false })
towerDrpRequest.interceptors.response.use(responseInterceptor(towerDrpRequest), {
    global: false
})
// inner 塔内售后服务
const towerAftersaleRequest = extend({
    prefix: presentBasePrefix(PROJECT_CONFIG.baseTowerAftersaleApi),
    errorHandler,
    paramsSerializer
})

towerAftersaleRequest.interceptors.request.use(tokenInterceptor(towerAftersaleRequest), { global: false })
towerAftersaleRequest.interceptors.response.use(responseInterceptor(towerAftersaleRequest), {
    global: false
})

// inner 塔内打单服务
const towerPrinterRequest = extend({
    prefix: presentBasePrefix(PROJECT_CONFIG.baseTowerPrinterApi),
    errorHandler,
    paramsSerializer
})

towerPrinterRequest.interceptors.request.use(tokenInterceptor(towerPrinterRequest), { global: false })
towerPrinterRequest.interceptors.response.use(responseInterceptor(towerPrinterRequest), {
    global: false
})

// 打单服务
const printerRequest = extend({
    prefix: presentBasePrefix(PROJECT_CONFIG.basePrinterApi),
    errorHandler,
    paramsSerializer
})

printerRequest.interceptors.request.use(tokenInterceptor(printerRequest), { global: false })
printerRequest.interceptors.response.use(responseInterceptor(printerRequest), {
    global: false
})

// 支付服务
const payRequest = extend({
    prefix: presentBasePrefix(PROJECT_CONFIG.basePayApi),
    errorHandler,
    paramsSerializer
})

payRequest.interceptors.request.use(tokenInterceptor(payRequest), { global: false })
payRequest.interceptors.response.use(responseInterceptor(payRequest), {
    global: false
})

// 内部配置服务
const insideRequest = extend({
    credentials: 'include',
    prefix: PROJECT_CONFIG.baseInsideApi,
    errorHandler,
    paramsSerializer
})

const SERVER_ERRPR_CODE = 5000

const insideResponseInterceptor = () => response => {
    if (response.code === SERVER_ERRPR_CODE) {
        notification.error({
            key: 'requestError',
            message: `请求错误`,
            description: response.message
        })
        return Promise.reject(createError({ response }))
    }
    if (response.code === 500) {
        $message.error(response.msg || '请求参数不正确')
    }
    if (response.act === 5) {
        $message.error(response.msg)
        return
    }
    return response
}
insideRequest.interceptors.request.use(tokenInterceptor(insideRequest), { global: false })
insideRequest.interceptors.response.use(responseInterceptor(insideRequest), { global: false })
insideRequest.interceptors.response.use(insideResponseInterceptor(insideRequest), { global: false })

wrap(insideRequest.interceptors.request)
wrap(insideRequest.interceptors.response)

// api/auth 服务
const securityRequest = extend({
    prefix: presentBasePrefix(PROJECT_CONFIG.baseSecurityApi),
    errorHandler,
    paramsSerializer
})

securityRequest.interceptors.request.use(tokenInterceptor(securityRequest), { global: false })
securityRequest.interceptors.response.use(responseInterceptor(securityRequest), {
    global: false
})

wrap(securityRequest.interceptors.request)
wrap(securityRequest.interceptors.response)

// 开放请求
const openSecurityRequest = extend({
    prefix: presentBasePrefix(PROJECT_CONFIG.baseSecurityApi),
    errorHandler,
    paramsSerializer,
    headers: { 'Content-Type': 'application/json', ...requestSource }
})

openSecurityRequest.interceptors.request.use(openInterceptor, { global: false })
openSecurityRequest.interceptors.response.use(openResponseInterceptor, { global: false })

// api/drp 服务
const drpRequest = extend({
    prefix: presentBasePrefix(PROJECT_CONFIG.baseDrpApi),
    errorHandler,
    paramsSerializer
})

drpRequest.interceptors.request.use(tokenInterceptor(drpRequest), { global: false })
drpRequest.interceptors.response.use(responseInterceptor(drpRequest), {
    global: false
})

wrap(drpRequest.interceptors.request)
wrap(drpRequest.interceptors.response)

// 开放请求 api/drp 服务
const openDrpRequest = extend({
    prefix: presentBasePrefix(PROJECT_CONFIG.baseDrpApi),
    errorHandler,
    paramsSerializer,
    headers: { 'Content-Type': 'application/json', ...requestSource }
})

openDrpRequest.interceptors.request.use(openInterceptor, { global: false })
openDrpRequest.interceptors.response.use(openResponseInterceptor, { global: false })

// 分销大店 /api/market 服务
const marketRequest = extend({
    prefix: presentBasePrefix(PROJECT_CONFIG.baseMarketApi),
    errorHandler,
    paramsSerializer
})
marketRequest.interceptors.request.use(tokenInterceptor(marketRequest), { global: false })
marketRequest.interceptors.response.use(responseInterceptor(marketRequest), { global: false })

// 分销大店 /api/market 服务
const openMarketRequest = extend({
    prefix: presentBasePrefix(PROJECT_CONFIG.baseMarketApi),
    errorHandler,
    paramsSerializer
})
openMarketRequest.interceptors.request.use(openInterceptor, { global: false })
openMarketRequest.interceptors.response.use(openResponseInterceptor, { global: false })

// scmops服务 api/scmops 服务
const scmopRequest = extend({
    prefix: presentBasePrefix(PROJECT_CONFIG.baseScmopsApi),
    errorHandler,
    paramsSerializer
})
scmopRequest.interceptors.request.use(tokenInterceptor(scmopRequest), { global: false })
scmopRequest.interceptors.response.use(responseInterceptor(scmopRequest), { global: false })

const allRequestInstances = [
    companyRequest,
    drpRequest,
    goodsBasicRequest,
    goodsRequest,
    insideRequest,
    marketRequest,
    noPrefixRequest,
    openCompanyRequest,
    openDrpRequest,
    openGoodsRequest,
    openOpRequest,
    openSecurityRequest,
    openMarketRequest,
    opRequest,
    payRequest,
    printerRequest,
    scmopRequest,
    securityRequest,
    towerAftersaleRequest,
    towerPrinterRequest,
    towerDrpRequest,
    towerRequest
]

// 打印进程资源会因为添加header信息报错
function isWhiteList(url) {
    return url.includes('//127.0.0.1')
}

allRequestInstances.forEach(instance => {
    instance.use(
        async (ctx, next) => {
            const { req } = ctx
            const { options } = req

            if (!isWhiteList(req.url)) {
                const visitorId = await getVisitorId()

                ctx.req.options = {
                    ...options,
                    headers: {
                        ...(options.headers || {}),
                        gwfp: visitorId,
                        ...apiHeadersObject
                    }
                }
            }

            await next()
        },
        { global: false }
    )
})

/**
 * 注意：
 * 添加新实列时，请确保添加到allRequestInstances数组中,防止统一拦截器丢失
 */

export {
    companyRequest,
    drpRequest,
    goodsBasicRequest,
    goodsRequest,
    insideRequest,
    marketRequest,
    noPrefixRequest,
    openCompanyRequest,
    openDrpRequest,
    openGoodsRequest,
    openMarketRequest,
    openOpRequest,
    openSecurityRequest,
    opRequest,
    payRequest,
    printerRequest,
    scmopRequest,
    securityRequest,
    towerAftersaleRequest,
    towerDrpRequest,
    towerPrinterRequest,
    towerRequest
}
